/**
 * animations.js — Summit cinematic motion system.
 * Requires: gsap.min.js + ScrollTrigger.min.js (loaded via defer before this file).
 * Fully respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion) {
    document.querySelectorAll('.js-reveal').forEach(function (el) {
      el.classList.add('js-reveal--visible');
    });
    return;
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      /* GSAP unavailable — fall back: show all js-reveal elements */
      document.querySelectorAll('.js-reveal').forEach(function (el) {
        el.classList.add('js-reveal--visible');
      });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    gsap.defaults({ ease: 'power3.out' });

    initPageEntrance();
    initHeroSequence();
    initHeroParallax();
    initSectionHeadings();
    initBatchReveals();
    initGenericReveals();

    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });

    /* Re-init after cart re-renders product grids into the page */
    document.addEventListener('cart:rendered', function () {
      initBatchReveals();
      ScrollTrigger.refresh();
    });
  });

  /* ------------------------------------------------------------------
     Page entrance
     Body fades from invisible on first paint for a clean load feel.
     clearProps ensures no inline style lingers after the tween.
     ------------------------------------------------------------------ */
  function initPageEntrance() {
    gsap.from(document.body, {
      autoAlpha: 0,
      duration: 0.45,
      clearProps: 'opacity,visibility'
    });
  }

  /* ------------------------------------------------------------------
     Hero entrance — coordinated timeline, no scroll dependency
     Choreography: eyebrow → heading words → subheading → CTAs
     ------------------------------------------------------------------ */
  function initHeroSequence() {
    var hero = document.querySelector('.hero-banner');
    if (!hero) return;

    var eyebrow    = hero.querySelector('.hero-banner__eyebrow');
    var headingEl  = hero.querySelector('.hero-banner__heading');
    var subheading = hero.querySelector('.hero-banner__subheading');
    var ctasEl     = hero.querySelector('.hero-banner__ctas');

    var tl = gsap.timeline({ delay: 0.1 });

    if (eyebrow) {
      gsap.set(eyebrow, { autoAlpha: 0, y: 16 });
      tl.to(eyebrow, { autoAlpha: 1, y: 0, duration: 0.55 }, 0.2);
    }

    if (headingEl) {
      /* Richtext field outputs <h1> or <h2> inside .hero-banner__heading */
      var inner = headingEl.querySelector('h1, h2') || headingEl;
      var words = splitWords(inner);
      gsap.set(words, { yPercent: 110, autoAlpha: 0 });
      tl.to(words, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 0.8,
        ease: 'power4.out',
        stagger: 0.045
      }, 0.35);
    }

    if (subheading) {
      gsap.set(subheading, { autoAlpha: 0, y: 18, filter: 'blur(4px)' });
      tl.to(subheading, {
        autoAlpha: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7
      }, 0.7);
    }

    if (ctasEl) {
      var btns = Array.from(ctasEl.children);
      gsap.set(btns, { autoAlpha: 0, y: 12, scale: 0.97 });
      tl.to(btns, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.55,
        ease: 'power2.out',
        stagger: 0.1
      }, 0.9);
    }
  }

  /* ------------------------------------------------------------------
     Hero parallax
     Background image layer moves at 80% the scroll speed of content,
     creating a subtle depth separation.
     invalidateOnRefresh recalculates on resize.
     ------------------------------------------------------------------ */
  function initHeroParallax() {
    var heroBg = document.querySelector('.hero-banner__bg-media');
    if (!heroBg) return;

    gsap.to(heroBg, {
      yPercent: 20,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero-banner',
        start: 'top top',
        end: 'bottom top',
        scrub: 1.2,
        invalidateOnRefresh: true
      }
    });
  }

  /* ------------------------------------------------------------------
     Section headings — word-by-word masked reveal on scroll entry
     Each word rises from below its clipping container.
     ------------------------------------------------------------------ */
  function initSectionHeadings() {
    document.querySelectorAll('.section-heading__title').forEach(function (heading) {
      var words = splitWords(heading);
      gsap.set(words, { yPercent: 110, autoAlpha: 0 });

      ScrollTrigger.create({
        trigger: heading,
        start: 'top 86%',
        once: true,
        onEnter: function () {
          gsap.to(words, {
            yPercent: 0,
            autoAlpha: 1,
            duration: 0.75,
            stagger: 0.045
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Batch reveals — product cards and category tiles
     ScrollTrigger.batch groups elements that enter the viewport
     in the same frame and fires one tween for the whole batch,
     creating a natural wave rather than a rigid grid pop.
     ------------------------------------------------------------------ */
  function initBatchReveals() {
    /* Product cards */
    ScrollTrigger.batch('.product-card:not(.motion-revealed)', {
      onEnter: function (batch) {
        gsap.fromTo(
          batch,
          { autoAlpha: 0, y: 32, scale: 0.97 },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 0.85,
            stagger: 0.07,
            onComplete: function () {
              batch.forEach(function (el) {
                el.classList.add('motion-revealed');
              });
            }
          }
        );
      },
      start: 'top 92%',
      once: true
    });

    /* Category tiles */
    ScrollTrigger.batch('.category-tile:not(.motion-revealed)', {
      onEnter: function (batch) {
        gsap.fromTo(
          batch,
          { autoAlpha: 0, y: 24 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.75,
            stagger: 0.06,
            onComplete: function () {
              batch.forEach(function (el) {
                el.classList.add('motion-revealed');
              });
            }
          }
        );
      },
      start: 'top 88%',
      once: true
    });
  }

  /* ------------------------------------------------------------------
     Generic reveals — existing .js-reveal elements
     clearProps removes inline transform/opacity after animation
     so hover states defined in CSS take over cleanly.
     ------------------------------------------------------------------ */
  function initGenericReveals() {
    document.querySelectorAll('.js-reveal').forEach(function (el) {
      gsap.set(el, { autoAlpha: 0, y: 24 });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.to(el, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out',
            clearProps: 'transform,opacity,visibility'
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Word split helper
     Wraps each word in an overflow:hidden mask (.motion-word-mask)
     with an inner animated span (.motion-word).
     Stores aria-label on the parent so screen readers read the
     original text regardless of the DOM restructuring.
     Idempotent — safe to call multiple times on same element.
     ------------------------------------------------------------------ */
  function splitWords(el) {
    if (el.dataset.motionSplit === 'true') {
      return el.querySelectorAll('.motion-word');
    }

    var text = (el.textContent || '').trim();
    el.setAttribute('aria-label', text);
    el.innerHTML = '';

    text.split(/\s+/).forEach(function (word, i) {
      var mask  = document.createElement('span');
      var inner = document.createElement('span');

      mask.className = 'motion-word-mask';
      mask.setAttribute('aria-hidden', 'true');

      inner.className = 'motion-word';
      inner.textContent = word;
      inner.style.setProperty('--word-index', i);

      mask.appendChild(inner);

      if (i > 0) el.appendChild(document.createTextNode(' '));
      el.appendChild(mask);
    });

    el.dataset.motionSplit = 'true';
    return el.querySelectorAll('.motion-word');
  }

})();
